import React, { useState, useEffect } from 'react'
import { FormVersionService } from '../../../services/formVersionService'
import type { FormVersion } from '../../../services/formVersionService'

interface FormVersionListProps {
  onNavigate: (page: string, data?: any) => void
}

export function FormVersionList({ onNavigate }: FormVersionListProps) {
  const [formVersions, setFormVersions] = useState<FormVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [keyword, setKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [sortBy, setSortBy] = useState('updatedAt_desc')

  useEffect(() => {
    loadFormVersions()
  }, [keyword, statusFilter, sortBy])

  const loadFormVersions = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await FormVersionService.getAll({
        q: keyword || undefined,
        status: statusFilter || undefined
      })

      // フロントエンドでソート
      const sorted = [...data].sort((a, b) => {
        const [field, direction] = sortBy.split('_')
        let aValue: any = a[field as keyof FormVersion]
        let bValue: any = b[field as keyof FormVersion]

        // 日付の場合は比較用に変換
        if (field === 'createdAt' || field === 'updatedAt') {
          aValue = new Date(aValue).getTime()
          bValue = new Date(bValue).getTime()
        }

        if (direction === 'asc') {
          return aValue > bValue ? 1 : -1
        } else {
          return aValue < bValue ? 1 : -1
        }
      })

      setFormVersions(sorted)
    } catch (err) {
      console.error('Failed to load form versions:', err)
      setError('Failed to load form versions')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    onNavigate('form-version-detail')
  }

  const handleView = (formVersionId: string) => {
    onNavigate('form-version-detail', { formVersionId })
  }

  const SORT_OPTIONS = [
    { value: 'createdAt_desc', label: '作成日（新しい順）' },
    { value: 'createdAt_asc', label: '作成日（古い順）' },
    { value: 'updatedAt_desc', label: '更新日（新しい順）' },
    { value: 'updatedAt_asc', label: '更新日（古い順）' },
    { value: 'formName_asc', label: 'フォーム名（A-Z）' },
    { value: 'formName_desc', label: 'フォーム名（Z-A）' }
  ]

  const getStatusBadge = (status: string) => {
    const colors = {
      DRAFT: 'bg-gray-100 text-gray-800',
      PUBLISHED: 'bg-green-100 text-green-800',
      ARCHIVED: 'bg-red-100 text-red-800'
    }
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="px-8 py-6">
      <div style={{
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '0.75rem',
        marginBottom: '2rem'
      }}>
        <h1 className="page-title">フォーム管理</h1>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #e5e7eb',
          color: '#b91c1c',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {error}
        </div>
      )}

      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '1rem',
          alignItems: 'end'
        }}>
          {/* キーワード検索 */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>キーワード検索</label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="IDまたは名前で検索"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = 'none'
              }}
            />
          </div>

          {/* ステータス */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>ステータス</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              <option value="">すべて</option>
              <option value="DRAFT">編集中</option>
              <option value="PUBLISHED">確定</option>
              <option value="ARCHIVED">廃止</option>
            </select>
          </div>
        </div>

        {/* ソート選択と件数表示 */}
        <div style={{
          marginTop: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 7L12 3L16 7M8 17L12 21L16 17" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              paddingLeft: '0.75rem',
              paddingRight: '2rem',
              paddingTop: '0.5rem',
              paddingBottom: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              backgroundColor: 'white',
              cursor: 'pointer',
              outline: 'none',
              transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#3b82f6'
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db'
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            {SORT_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div style={{
            fontSize: '0.875rem',
            color: '#6B7280',
            fontWeight: '500'
          }}>
            {formVersions.length}件のフォーム
          </div>
        </div>
      </div>

      {/* 作成ボタン */}
      <div className="mb-4">
        <button
          onClick={handleCreate}
          className="btn btn-primary"
        >
          + 新規作成
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">読み込み中...</div>
        ) : formVersions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">フォームが見つかりません</div>
        ) : (
          <table className="min-w-full" style={{ borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f8fafc' }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>フォームID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>ステータス</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>フォーム名</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>作成日時</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>更新日時</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>更新者</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>アクション</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {formVersions.map((form) => (
                <tr key={form.formVersionId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {form.formVersionId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadge(form.status)}`}>
                      {form.status === 'DRAFT' ? '編集中' : form.status === 'PUBLISHED' ? '確定' : '廃止'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {form.formName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {new Date(form.createdAt).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {new Date(form.updatedAt).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {form.updatedBy}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    <button
                      onClick={() => handleView(form.formVersionId)}
                      style={{
                        padding: '0.375rem 0.75rem',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                    >
                      開く
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
